import React, { useCallback, useEffect, useState, type ComponentType } from 'react';
import { JSErrorBoundary } from '@atlassian/jira-error-boundaries/src/ui/js-error-boundary/JSErrorBoundary.tsx';
import { useIntl } from '@atlassian/jira-intl';
import Placeholder from '@atlassian/jira-placeholder/src/index.tsx';
import { lazyForPaint } from '@atlassian/react-loosely-lazy';
import { useTopMenus as useTopMenusDI } from '@atlassian/jira-navigation-apps-common/src/controllers/top-menus/index.tsx';
import { MENU_ID } from '@atlassian/jira-navigation-apps-common/src/constants.tsx';
import { getWillShowNav4 } from '@atlassian/jira-navigation-apps-sidebar-nav4-rollout-core/src/common/utils/get-will-show-nav4/index.tsx';
import { componentWithCondition } from '@atlassian/jira-feature-flagging-utils';
import { expVal } from '@atlassian/jira-feature-experiments';
import UFOSegment from '@atlassian/jira-ufo-segment/src/index.tsx';
import { PACKAGE_NAME } from '../../common/constants.tsx';
import messages from './messages.tsx';
import {
	searchDialogMetric,
	SEARCH_DIALOG_DOWNLOAD_START_MARK,
	SEARCH_DIALOG_DOWNLOAD_END_MARK,
} from './performance-metrics/index.tsx';
import { Skeleton } from './skeleton/index.tsx';
import type { SearchProps as SearchPropsType } from './types.tsx';
import { SkeletonInteractive } from './skeleton/interactive.tsx';

const LoadingSkeleton = () => {
	const { formatMessage } = useIntl();
	return (
		<Skeleton
			placeholder={formatMessage(messages.interactiveSearchSkeletonPlaceholder)}
			ariaLabel={formatMessage(messages.interactiveSearchSkeletonAriaLabel)}
		/>
	);
};

const LoadingSkeletonInteractive = () => {
	const { formatMessage } = useIntl();
	return (
		<SkeletonInteractive
			placeholder={formatMessage(messages.interactiveSearchSkeletonPlaceholder)}
			ariaLabel={formatMessage(messages.interactiveSearchSkeletonAriaLabel)}
		/>
	);
};

const SearchDialogMetricStart = () => {
	useEffect(() => {
		searchDialogMetric.startFromPageLoad();
		searchDialogMetric.mark(SEARCH_DIALOG_DOWNLOAD_START_MARK);
	}, []);

	return null;
};

const SearchDialogMetricEnd = () => {
	useEffect(() => {
		searchDialogMetric.mark(SEARCH_DIALOG_DOWNLOAD_END_MARK);
	}, []);

	return null;
};

// eslint-disable-next-line jira/deprecations/no-rll-client-async-experiences
const LazySearch = lazyForPaint<ComponentType<SearchPropsType>>(
	() =>
		import(/* webpackChunkName: "async-atlassian-navigation-search" */ './main').then(
			(m) => m.Search,
		),
	{ ssr: false },
);

export const SearchInner = (props: SearchPropsType) => (
	<JSErrorBoundary id="search" packageName={PACKAGE_NAME} fallback="unmount">
		<SearchDialogMetricStart />
		<Placeholder
			name="lazy-search"
			fallback={
				expVal('jfp-magma-concurrent-load-search-on-intent-exp', 'isEnabled', false) ? (
					<LoadingSkeletonExpandable useTopMenus={props.useTopMenus} />
				) : (
					<LoadingSkeleton />
				)
			}
		>
			<LazySearch {...props} />
			<SearchDialogMetricEnd />
		</Placeholder>
	</JSErrorBoundary>
);

const loadingSkeletonExpandableWrapperStyle = { width: '100%' } as const; // only for nav4

export const LoadingSkeletonExpandable = ({
	setShowRealExperience,
	useTopMenus = useTopMenusDI,
}: Pick<SearchPropsType, 'useTopMenus'> & {
	setShowRealExperience?: (showRealExperience: boolean) => void;
}) => {
	const [_, { on: openMenu }] = useTopMenus(MENU_ID.SEARCH);
	const onClick = useCallback(() => {
		setShowRealExperience?.(true);
		openMenu();
	}, [setShowRealExperience, openMenu]);

	const onMouseOver = useCallback(() => {
		setShowRealExperience?.(true);
	}, [setShowRealExperience]);
	return (
		// eslint-disable-next-line jsx-a11y/no-static-element-interactions, @atlassian/a11y/interactive-element-not-keyboard-focusable
		<div
			data-testid="atlassian-navigation.ui.search.loading-skeleton-expandable"
			onClick={onClick}
			onKeyDown={onClick}
			onMouseOver={onMouseOver}
			onFocus={onClick} // we use onClick instead of onMouseOver to expand the dialog on focus as the interactive skeleton focus expands - if we dont expand we get a flicker
			// eslint-disable-next-line jira/react/no-style-attribute, @atlaskit/ui-styling-standard/enforce-style-prop
			style={getWillShowNav4() ? loadingSkeletonExpandableWrapperStyle : undefined}
		>
			<LoadingSkeletonInteractive />
		</div>
	);
};

export const SearchLoadOnIntent = (props: SearchPropsType) => {
	const { useTopMenus = useTopMenusDI } = props;
	const [isSearchMenuOpen] = useTopMenus(MENU_ID.SEARCH);
	const [showRealExperience, setShowRealExperience] = useState(isSearchMenuOpen);

	useEffect(() => {
		if (isSearchMenuOpen) {
			setShowRealExperience(true);
		}
	}, [isSearchMenuOpen, setShowRealExperience]);

	return (
		<UFOSegment name="atlassian-navigation-search">
			{showRealExperience ? (
				<UFOSegment name="atlassian-navigation-search-experience">
					<SearchInner {...props} />
				</UFOSegment>
			) : (
				<UFOSegment name="atlassian-navigation-search-loading-skeleton-expandable">
					<LoadingSkeletonExpandable
						useTopMenus={useTopMenus}
						setShowRealExperience={setShowRealExperience}
					/>
				</UFOSegment>
			)}
		</UFOSegment>
	);
};

export const Search = componentWithCondition(
	() => expVal('jfp-magma-concurrent-load-search-on-intent-exp', 'isEnabled', false),
	SearchLoadOnIntent,
	SearchInner,
);

// eslint-disable-next-line @atlassian/eng-health/no-barrel-files/disallow-reexports
export type { SearchProps } from './types';
